import React from 'react';
import {useParams} from 'react-router';
import {useMutation, useQuery} from '@apollo/client';
import gql from 'graphql-tag';
import ReactDocumentTitle from '../../../components/ReactDocumentTitle';
import AppLayout from '../../../layouts/AppLayout';
import BackLink from '../../../ui/BackLink';
import {handleError, handleSuccess} from '../../../utils/Apollo';
import FormTitle from '../../../ui/FormTitle';
import AgreementForm from '../../../components/licenseType/AgreementForm';
import Preloader from '../../../ui/Preloader';
import {LicenseType} from '../../../models/brand/LicenseType';
import {reverse} from '../../../urls';
import Menu from '../../../components/licenseType/Menu';
import FormWrapper from '../../../components/licenseType/FormWrapper';
import BackForward from '../../../components/licenseType/BackForward';

interface PathParams {
  licenseTypeId: string
}

export default function AddAgreementScene() {
  const {licenseTypeId} = useParams<PathParams>()
  const {loading, data, error} = useQuery<FormData>(query, {
    variables: {id: licenseTypeId},
    errorPolicy: 'all'
  });

  const [submit] = useMutation(mutation);
  const backLink = reverse('brand_settings_license_types');

  const onFormSubmit = React.useCallback((formData) => {
    submit({
      variables: {data: formData, licenseTypeId}
    }).then(
      () => handleSuccess('License agreement added', reverse('license_renewal_period_edit', {id: licenseTypeId})),
      (apolloError) => handleError(apolloError)
    );
  }, [submit, licenseTypeId]);

  return (
    <>
      <ReactDocumentTitle title="Add license agreement"/>
      <AppLayout className="bg-gray-light">
        <BackLink backLink={reverse('brand_settings_license_types')} title="Back to settings"/>
        {error && <Preloader/>}
        {loading && <Preloader/>}
        {!loading && !error &&
          <>
            <Menu activeIndex={3}/>
            <FormWrapper>
              <FormTitle>Add license agreement</FormTitle>
              <p className="mb-2">When trainers try to renew their expiring licenses inside
                Workshop Butler, they need to accept a license agreement. By giving their consent, they agree
                with the terms of the agreement.
              </p>
              <p className="mb-8">
                Without the agreement, trainers are not allowed to renew licenses. You have to do it for them.</p>
              <AgreementForm onSubmit={onFormSubmit} backLink={backLink}/>
              <BackForward
                back={{url: reverse('license_renewal_templates_edit', {licenseTypeId}), label: 'Edit Notifications'}}/>
            </FormWrapper>
          </>
        }
      </AppLayout>
    </>
  );
}

interface FormData {
  licenseType?: LicenseType
}


const query = gql`
  query LicenseTypesFormQuery($id: ID!) {
    licenseType(id: $id) {
      id
      name
    }
  }
`;

const mutation = gql`
  mutation AddFormMutation($licenseTypeId: ID!, $data: LicenseAgreementInput!) {
    createLicenseAgreement(licenseTypeId: $licenseTypeId, data: $data) {
      id
      version
      content
      consent
    }
  }
`;
